import React, { useCallback, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import { CreateInternalAgentDocument } from "@/generated/graphql";
import { assert, assertInRange, assertNotEmpty, assertOneOf } from "@/utils/assertions";
import { sanitizeLength, sanitizeText } from "@/utils/sanitization";
import { RateLimitPresets, checkRateLimit } from "@/utils/rateLimiter";
import { useDebounce, useOperationLock } from "@/utils/stateManagement";
import { multilineItemsSchema, stringWithLength, z } from "@/utils/zod-schemas";
import { maxLength, minLength, validateMultilineItems, validateSafeContent } from "@/utils/validation";

import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/navbar";
import { InputWithCount } from "@/components/ui/input-with-count";
import { TextareaWithCount } from "@/components/ui/textarea-with-count";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// import { Badge } from "@/components/ui/badge";

// Valid agent types
const VALID_AGENT_TYPES = ['SPECIALIZED', 'WORKFLOW', 'INTEGRATION', 'UTILITY'] as const;
type AgentType = typeof VALID_AGENT_TYPES[number];

// Form field constraints
const CONSTRAINTS = {
  name: { min: 3, max: 50 },
  description: { min: 20, max: 500 },
  systemPrompt: { min: 50, max: 2000 },
  capabilities: { min: 1, max: 10 },
  tags: { min: 0, max: 10 },
} as const;

// Zod schema for form validation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const internalSAFormSchema = z.object({
  name: stringWithLength(CONSTRAINTS.name.min, CONSTRAINTS.name.max, 'Agent name'),
  description: stringWithLength(CONSTRAINTS.description.min, CONSTRAINTS.description.max, 'Description'),
  type: z.enum(VALID_AGENT_TYPES),
  capabilities: multilineItemsSchema(CONSTRAINTS.capabilities.min, CONSTRAINTS.capabilities.max),
  systemPrompt: stringWithLength(CONSTRAINTS.systemPrompt.min, CONSTRAINTS.systemPrompt.max, 'System prompt'),
  tags: z.string().optional(),
});

type InternalSAFormData = z.infer<typeof internalSAFormSchema>;

export function InternalSAQuickCreate() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { acquireLock } = useOperationLock();
  const formRef = useRef<HTMLFormElement>(null);
  
  // Track component lifecycle for cleanup
  const isMountedRef = useRef(true);
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<InternalSAFormData>({
    defaultValues: {
      type: "SPECIALIZED",
    },
  });

  // Watch form values for character count indicators
  const watchedValues = watch(['name', 'description', 'systemPrompt', 'capabilities', 'tags']);

  // GraphQL mutation for creating internal agents
  const [createInternalAgent] = useMutation(CreateInternalAgentDocument);

  const validateFormData = useCallback((data: InternalSAFormData): void => {
    // Pre-conditions: Validate all inputs
    assertNotEmpty(data.name, 'Agent name');
    assertInRange(data.name.length, CONSTRAINTS.name.min, CONSTRAINTS.name.max, 'Agent name length');
    
    assertNotEmpty(data.description, 'Description');
    assertInRange(data.description.length, CONSTRAINTS.description.min, CONSTRAINTS.description.max, 'Description length');
    
    assertOneOf(data.type as AgentType, VALID_AGENT_TYPES, 'Agent type');
    
    // Capabilities can be either string or array depending on the form state
    if (typeof data.capabilities === 'string') {
      assertNotEmpty(data.capabilities, 'Capabilities');
    } else {
      assert(data.capabilities.length > 0, 'At least one capability is required');
    }
    
    assertNotEmpty(data.systemPrompt, 'System prompt');
    assertInRange(data.systemPrompt.length, CONSTRAINTS.systemPrompt.min, CONSTRAINTS.systemPrompt.max, 'System prompt length');
  }, []);

  const onSubmit = async (data: InternalSAFormData) => {
    // Check rate limit
    const userId = 'current-user'; // TODO: Get from auth context
    if (!checkRateLimit(userId, 'create-internal-agent', RateLimitPresets.FORM_SUBMIT)) {
      toast.error('Too many submissions. Please wait a moment and try again.');
      return;
    }

    try {
      // Acquire lock to prevent double submission
      await acquireLock('create-internal-agent', async () => {
        setIsSubmitting(true);
        
        // Validate form data
        validateFormData(data);
        
        // Sanitize all inputs
        const sanitizedData = {
          name: sanitizeLength(sanitizeText(data.name), CONSTRAINTS.name.max, 'Name'),
          description: sanitizeLength(sanitizeText(data.description), CONSTRAINTS.description.max, 'Description'),
          type: data.type,
          capabilities: data.capabilities,
          systemPrompt: sanitizeLength(sanitizeText(data.systemPrompt), CONSTRAINTS.systemPrompt.max, 'System prompt'),
          tags: data.tags,
        };
        // Parse and validate tags
        const tagArray = sanitizedData.tags
          ? (sanitizedData.tags as string)
              .split(',')
              .map(tag => sanitizeText(tag.trim()))
              .filter(tag => tag.length > 0)
              .slice(0, CONSTRAINTS.tags.max) // Limit number of tags
          : [];
        
        // Parse and validate capabilities (Zod schema already transforms to array)
        const capabilitiesArray = Array.isArray(sanitizedData.capabilities)
          ? sanitizedData.capabilities
          : (sanitizedData.capabilities as string)
              .split('\n')
              .map((cap: string) => sanitizeText(cap.trim()))
              .filter((cap: string) => cap.length > 0)
              .slice(0, CONSTRAINTS.capabilities.max); // Limit number of capabilities
        
        // Invariant: Must have at least one capability
        assert(capabilitiesArray.length >= CONSTRAINTS.capabilities.min, 'At least one capability is required');

        // Execute GraphQL mutation
        const result = await createInternalAgent({
          variables: {
            input: {
              name: sanitizedData.name,
              description: sanitizedData.description,
              type: sanitizedData.type,
              capabilities: capabilitiesArray,
              systemPrompt: sanitizedData.systemPrompt,
              tags: tagArray,
            },
          },
        });

        // Post-condition: Agent should be created successfully
        assert(result.data?.createInternalAgent.success, 'Agent creation should succeed');
        assert(result.data?.createInternalAgent.agent?.id, 'Created agent should have an ID');
        
        if (isMountedRef.current) {
          toast.success(result.data.createInternalAgent.message || 'Internal SA created successfully!');
          // Navigate to the agent details page
          navigate(`/sa/agents/${result.data.createInternalAgent.agent.id}`);
        }
      });
    } catch (error) {
      console.error('Error creating internal SA:', error);
      if (error instanceof Error) {
        toast.error(`Failed to create internal SA: ${error.message}`);
      } else {
        toast.error('Failed to create internal SA. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };
  
  // Debounced form validation for better UX
  const debouncedValidation = useDebounce((fieldName: string, value: string) => {
    try {
      if (fieldName === 'name') {
        assertNotEmpty(value, 'Agent name');
        assertInRange(value.length, CONSTRAINTS.name.min, CONSTRAINTS.name.max, 'Agent name length');
      } else if (fieldName === 'description') {
        assertNotEmpty(value, 'Description');
        assertInRange(value.length, CONSTRAINTS.description.min, CONSTRAINTS.description.max, 'Description length');
      } else if (fieldName === 'systemPrompt') {
        assertNotEmpty(value, 'System prompt');
        assertInRange(value.length, CONSTRAINTS.systemPrompt.min, CONSTRAINTS.systemPrompt.max, 'System prompt length');
      }
    } catch (_error) {
      // Validation errors will be shown by react-hook-form
    }
  }, 300);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container mx-auto max-w-3xl py-8">
      <Card>
        <CardHeader>
          <CardTitle>Create Internal Specialized Agent</CardTitle>
          <CardDescription>
            Quickly create a new internal specialized agent for the platform.
            Internal agents are immediately available without review.
          </CardDescription>
        </CardHeader>
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
          <CardContent className="space-y-6">
            <InputWithCount
              id="name"
              label="Agent Name"
              value={watchedValues[0] || ""}
              min={CONSTRAINTS.name.min}
              max={CONSTRAINTS.name.max}
              placeholder="e.g., Code Reviewer Pro"
              error={errors.name?.message}
              helperText="Choose a descriptive name for your agent"
              {...register('name', {
                required: 'Agent name is required',
                minLength: minLength(CONSTRAINTS.name.min),
                maxLength: maxLength(CONSTRAINTS.name.max),
                validate: validateSafeContent,
                onChange: (e) => debouncedValidation('name', e.target.value),
              })}
            />

            <TextareaWithCount
              id="description"
              label="Description"
              value={watchedValues[1] || ""}
              min={CONSTRAINTS.description.min}
              max={CONSTRAINTS.description.max}
              placeholder="Describe what this agent does and its key features..."
              rows={4}
              error={errors.description?.message}
              helperText="Provide a clear description to help users understand your agent's purpose"
              {...register('description', {
                required: 'Description is required',
                minLength: minLength(CONSTRAINTS.description.min),
                maxLength: maxLength(CONSTRAINTS.description.max),
                validate: validateSafeContent,
                onChange: (e) => debouncedValidation('description', e.target.value),
              })}
            />

            <div className="space-y-2">
              <Label htmlFor="type">Agent Type</Label>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Agent type is required" }}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger id="type" className="w-full">
                      <SelectValue placeholder="Select agent type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SPECIALIZED">Specialized</SelectItem>
                      <SelectItem value="WORKFLOW">Workflow</SelectItem>
                      <SelectItem value="INTEGRATION">Integration</SelectItem>
                      <SelectItem value="UTILITY">Utility</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.type && (
                <p className="text-sm text-destructive">{errors.type.message}</p>
              )}
            </div>

            <TextareaWithCount
              id="capabilities"
              label="Capabilities (one per line)"
              value={Array.isArray(watchedValues[3]) ? watchedValues[3].join('\n') : (watchedValues[3] || "")}
              min={CONSTRAINTS.capabilities.min}
              max={CONSTRAINTS.capabilities.max}
              placeholder="Code analysis&#10;Security scanning&#10;Performance optimization"
              rows={3}
              error={errors.capabilities?.message}
              helperText="List each capability on a separate line"
              {...register("capabilities", {
                required: "At least one capability is required",
                validate: (value) => {
                  if (typeof value === 'string') {
                    return validateMultilineItems(value, CONSTRAINTS.capabilities.min, CONSTRAINTS.capabilities.max);
                  }
                  // If already an array (from schema transformation), validate length
                  return value.length >= CONSTRAINTS.capabilities.min && value.length <= CONSTRAINTS.capabilities.max 
                    ? true 
                    : `Must have between ${CONSTRAINTS.capabilities.min} and ${CONSTRAINTS.capabilities.max} capabilities`;
                },
              })}
            />

            <TextareaWithCount
              id="systemPrompt"
              label="System Prompt"
              value={watchedValues[2] || ""}
              min={CONSTRAINTS.systemPrompt.min}
              max={CONSTRAINTS.systemPrompt.max}
              placeholder="Define the agent's behavior and instructions..."
              rows={6}
              error={errors.systemPrompt?.message}
              helperText="Provide detailed instructions that define how your agent should behave"
              {...register('systemPrompt', {
                required: 'System prompt is required',
                minLength: minLength(CONSTRAINTS.systemPrompt.min),
                maxLength: maxLength(CONSTRAINTS.systemPrompt.max),
                validate: validateSafeContent,
                onChange: (e) => debouncedValidation('systemPrompt', e.target.value),
              })}
            />

            <InputWithCount
              id="tags"
              label="Tags (comma-separated)"
              value={watchedValues[4] || ""}
              max={200}
              placeholder="development, testing, automation"
              helperText="Add tags to help users discover your agent"
              {...register("tags")}
            />

            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm font-medium">Internal Agent Benefits:</p>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                <li>• Immediate availability - no review required</li>
                <li>• Full platform integration access</li>
                <li>• Priority support and maintenance</li>
                <li>• Enhanced performance metrics tracking</li>
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/sa")}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isSubmitting ? "Creating..." : "Create Agent"}
            </Button>
          </CardFooter>
        </form>
      </Card>
      </div>
    </div>
  );
}